import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";

class APIError extends Error {
  public status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "APIError";
    this.status = status;
  }
}

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OcrSummaryData {
  total_documents: number;
  avg_overall_accuracy: number;
  avg_header_accuracy: number;
  avg_gl_accuracy: number;
  avg_fields_edited: number;
  zero_edit_count: number;
  zero_edit_rate_pct: number;
  vendor_id_edits: number;
  bill_number_edits: number;
  bill_date_edits: number;
  total_amount_edits: number;
  gst_type_edits: number;
  gl_edits: number;
}

export interface OcrSummaryResponse {
  success: boolean;
  data: OcrSummaryData;
}

export interface OcrTrendMonth {
  month: string;
  total_documents: number;
  avg_header_accuracy: number;
  avg_gl_accuracy: number;
  avg_overall_accuracy: number;
  avg_fields_edited: number;
  zero_edit_rate_pct: number;
}

export interface OcrTrendResponse {
  success: boolean;
  data: OcrTrendMonth[];
}

export interface FieldBreakdownItem {
  field: string;
  edit_count: number;
  edit_rate: number;
}

export interface ExtractionMethodRow {
  extraction_method: string;
  total_documents: number;
  avg_accuracy: number;
  avg_edits: number;
  zero_edit_rate_pct: number;
}

export interface OcrFieldBreakdownResponse {
  success: boolean;
  data: {
    field_breakdown: FieldBreakdownItem[];
    extraction_comparison: ExtractionMethodRow[];
  };
}

// ── API functions ─────────────────────────────────────────────────────────────

const buildParams = (from: string, to: string) =>
  `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;

export const getOcrSummary = async (
  from: string,
  to: string
): Promise<OcrSummaryResponse> => {
  const res = await fetch(
    `${API_BASE_URL}/api/ocr-quality/summary?${buildParams(from, to)}`,
    { headers: authHeaders() }
  );
  const data = await res.json();
  if (!res.ok) throw new APIError(data.message || "Failed to fetch summary", res.status);
  return data as OcrSummaryResponse;
};

export const getOcrTrend = async (
  from: string,
  to: string
): Promise<OcrTrendResponse> => {
  const res = await fetch(
    `${API_BASE_URL}/api/ocr-quality/trend?${buildParams(from, to)}`,
    { headers: authHeaders() }
  );
  const data = await res.json();
  if (!res.ok) throw new APIError(data.message || "Failed to fetch trend", res.status);
  return data as OcrTrendResponse;
};

export const getOcrFieldBreakdown = async (
  from: string,
  to: string
): Promise<OcrFieldBreakdownResponse> => {
  const res = await fetch(
    `${API_BASE_URL}/api/ocr-quality/field-breakdown?${buildParams(from, to)}`,
    { headers: authHeaders() }
  );
  const data = await res.json();
  if (!res.ok)
    throw new APIError(data.message || "Failed to fetch field breakdown", res.status);
  return data as OcrFieldBreakdownResponse;
};
